import { ENTDWindow } from "./ENTDWindow";

declare const window: ENTDWindow;

const badgeTagName = "camp-aktionsflag";
const url = "/entd/campaigns/api/badges";
const badgesReadyEvent = "camp:aktionsflags:loader:ready";
const badgesInitializedEvent = "camp:aktionsflags:initialized";
const badgeEvent = "camp:aktionsflags:zuordnung:received";

class Badge extends HTMLElement {
    private static observedAttributes: string[] = ["syan", "artikel-id", "farb-id"];
    private initialized: boolean;
    private badgeMap: Map<number, BadgesForProdukt>;
    private isRecoSlider: boolean;
    private filter: string | null = null;

    constructor() {
        super();
        this.initialized = false;
        this.badgeMap = new Map();
        this.isRecoSlider = this.hasAttribute("is-reco-slider");
    }

    connectedCallback(): void {
        window.CampBadgeData = window.CampBadgeData || [];
        this.register();

        document.addEventListener(badgeEvent, (e) => this.processEvent(e as CustomEvent<BadgesForProduktWithFilter>), {
            passive: true,
        });

        this.initialized = true;
    }

    private register(): void {
        const data = getSyan(this);
        const isServerSideRendered = this.getAttribute("server-side-rendered");
        if (data && window.CampBadgeData && isServerSideRendered !== "true") {
            window.CampBadgeData.push(data);
        }
    }

    private processEvent({ detail: badges }: CustomEvent<BadgesForProduktWithFilter>): void {
        this.filter = badges.filter;
        this.badgeMap.set(badges.syan, badges);
        this.renderFromMap();
    }

    private anyLevelContainsBadge(syan: number): boolean {
        const badges = this.badgeMap.get(syan);
        if (badges) {
            return !!(badges.syanAktion || Object.keys(badges.farbAktionen).length > 0 || Object.keys(badges.artikelAktionen).length > 0);
        } else {
            return false;
        }
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    private renderFromMap(): boolean {
        const attributes = getBadgeAttributes(this);
        if (attributes) {
            this.setAttribute("syan-contains-flag", String(this.anyLevelContainsBadge(attributes.syan)));

            switch (attributes.tag) {
                case BadgeTag.syan: {
                    const entry = this.badgeMap.get(attributes.syan);
                    if (entry?.syanAktion) {
                        this.renderBadge(entry.syanAktion, this.filter);
                        return true;
                    } else {
                        return false;
                    }
                }
                case BadgeTag.farbe: {
                    const entry = this.badgeMap.get(attributes.syan);
                    const farbBadge = entry?.farbAktionen[attributes.farbId];
                    if (farbBadge) {
                        this.renderBadge(farbBadge, this.filter);
                        return true;
                    } else {
                        return false;
                    }
                }
                case BadgeTag.artikel: {
                    const entry = this.badgeMap.get(attributes.syan);
                    const artikelBadge = entry?.artikelAktionen[attributes.artikelId];
                    if (artikelBadge) {
                        this.renderBadge(artikelBadge, this.filter);
                        return true;
                    } else {
                        return false;
                    }
                }
            }
        } else {
            return false;
        }
    }

    attributeChangedCallback(_name: string, _oldValue: string, _newValue: string): void {
        if (this.initialized) {
            const hasRendered = this.renderFromMap();
            if (!hasRendered) {
                this.register();
                this.innerHTML = "";
                this.setAttribute("contains-flag", "false");
                document.dispatchEvent(new CustomEvent(badgesInitializedEvent));
            }
        }
    }

    private templateString(text: string): string {
        return `<div class="bdk-badge-article" variant="coal">${text}</div>`;
    }

    private renderBadge(badges: BadgeContent[], filter: string | null): void {
        if (this.isRecoSlider && filter) {
            const filtered = badges.filter((value) => !value.includes(filter));
            this.innerHTML = filtered[0] ? `${this.templateString(filtered[0])}` : "";
        } else {
            this.innerHTML = `
        ${badges.map((text) => this.templateString(text)).join("&nbsp;")}`;
        }

        this.setAttribute("contains-flag", "true");
    }
}

function getSyan(badge: Badge): number | null {
    const syanString = badge.getAttribute("syan");
    if (!syanString) {
        return null;
    }
    return Number.isNaN(parseInt(syanString, 10)) ? null : parseInt(syanString, 10);
}

function getBadgeAttributes(badge: Badge): BadgeAttributes | null {
    const syan = getSyan(badge);
    if (!syan) {
        return null;
    }

    const artikelId = badge.getAttribute("artikel-id");
    const farbId = badge.getAttribute("farb-id");

    if (artikelId) {
        return { tag: BadgeTag.artikel, syan: syan, artikelId: artikelId };
    } else if (farbId) {
        return { tag: BadgeTag.farbe, syan: syan, farbId: farbId };
    } else {
        return { tag: BadgeTag.syan, syan: syan };
    }
}

export type BadgeAttributes =
    | { tag: BadgeTag.syan; syan: number; artikelIds?: never; farbIds?: never }
    | { tag: BadgeTag.artikel; syan: number; artikelId: string; farbId?: never }
    | { tag: BadgeTag.farbe; syan: number; artikelId?: never; farbId: string };

class BadgeLoader extends HTMLElement {
    private badgesInitializedEventAbortController: AbortController;

    constructor() {
        super();
        this.badgesInitializedEventAbortController = new AbortController();
    }

    connectedCallback(): void {
        window.CampBadgeData = window.CampBadgeData || [];
        document.addEventListener(badgesInitializedEvent, (_) => this.updateBadges()(), {
            passive: true,
            signal: this.badgesInitializedEventAbortController.signal,
        });

        this.registerBadgesInDOM();
        this.updateBadges()();
        document.dispatchEvent(new CustomEvent(badgesReadyEvent));
    }

    disconnectedCallback(): void {
        this.badgesInitializedEventAbortController.abort();
    }

    private registerBadgesInDOM(): void {
        const badges: Badge[] = Array.from(document.querySelectorAll<Badge>(badgeTagName));
        badges.forEach((badge) => {
            const data = getSyan(badge);
            if (data && window.CampBadgeData) {
                window.CampBadgeData.push(data);
            }
        });
    }

    private updateBadges(): () => void {
        let timer: string | number | NodeJS.Timeout;
        return () => {
            clearTimeout(timer);
            timer = setTimeout(() => this.updateBadgesDebounced(), 50);
        };
    }

    private updateBadgesDebounced(): void {
        if (window.CampBadgeData) {
            const requests: number[] = window.CampBadgeData;
            window.CampBadgeData = [];
            const pathPrefix = this.getAttribute("data-path-prefix");
            const filter = this.getAttribute("data-filter");

            if (requests.length === 0) {
                return;
            } else {
                void callBadgeApi(requests, pathPrefix).then((badges) => {
                    return badges.forEach((b) => {
                        document.dispatchEvent(
                            new CustomEvent(badgeEvent, {
                                detail: { ...b, filter: filter },
                            }),
                        );
                    });
                });
            }
        }
    }
}

enum BadgeTag {
    syan = "syan",
    artikel = "artikel",
    farbe = "farbe",
}

type BadgeContent = string;

type BadgesForProdukt = {
    syan: number;
    syanAktion: BadgeContent[];
    farbAktionen: Record<string, BadgeContent[]>;
    artikelAktionen: Record<string, BadgeContent[]>;
};

type BadgesForProduktWithFilter = BadgesForProdukt & {
    filter: string | null;
};

const callBadgeApi: (syans: number[], pathPrefix: string | null) => Promise<BadgesForProdukt[]> = async (syans: number[], pathPrefix: string | null) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 3000);
    const fetchUrl = pathPrefix ? `${pathPrefix}${url}` : url;
    const response = await fetch(fetchUrl, {
        method: "POST",
        signal: controller.signal,
        body: JSON.stringify(syans),
        headers: {
            "cube-pb-force": "1",
        },
    });
    clearTimeout(id);

    return (await response.json()) as BadgesForProdukt[];
};

customElements.define(badgeTagName, Badge);
customElements.define("camp-aktionsflags-loader", BadgeLoader);
